import { LotProcessStore, LotItemRef, isActive, isNotStarted, isFinished } from '../store/lot-process.store';
import { inject, signal } from '@angular/core';
import {
    Subject,
    Observable,
    map,
    merge,
    shareReplay,
    of,
    takeUntil,
    mergeMap,
    take, filter, concat, skipUntil, switchMap
} from 'rxjs';
import { CreateBidPayload } from '../domain/create-bid-payload';
import { Result, ok, err } from '@hereisyourgitanton/core';
import { action, exhaustiveCheck, onOk, calculateCurrentBid } from '@tradgine/utils';
import {
    Id,
    AuctionExtended,
    BidAccepted,
    TradingProcedureSnapshot,
    TradingProcedure, Auction, AutoBid, SetAutoBidPayload, TradingProcedureDocument, TradingProcedureRegistry,
    ParticipantRole, BidHistory, TradingProcedureStatusChanged, ConnectionStatus,
    TradingProcedureStatus, OfferResult,
} from '../domain/models';
import { TRADING_QUERY_TOKEN } from '../tokens/trading-query.token';
import { TRADING_COMMAND_TOKEN } from '../tokens/trading-command.token';
import { TRADING_EVENTS_TOKEN } from '../tokens/trading-events.token';
import { toObservable } from '@angular/core/rxjs-interop';

/*
    Часть магии с Id происходит из связи между Lot и Auction - они у них одинаковые.
    Будет ли это всегда правдой? Неизвестно.
    Сделать:
    ! К сокетам подрубаемся только тогда, когда ТП активна или опубликована
    ! Добавить проверку, что все аукционы закончены и мы заканчиваем ТП, отрубаемся от сокетов
    ! Статус должен возвращать payload ошибки при ошибке
 */

const SELLER_DEANON_STATUSES: TradingProcedureStatus[] = ['AwaitingDecision', 'Finished', 'Cancelled'];

export class TradingUseCase {
    private readonly tradingQuery = inject(TRADING_QUERY_TOKEN);
    private readonly tradingCommand = inject(TRADING_COMMAND_TOKEN);
    private readonly tradingEvents = inject(TRADING_EVENTS_TOKEN);

    private readonly lotProcessStore: LotProcessStore = new LotProcessStore();

    destroy$ = new Subject<void>();

    private readonly _status = signal<RuntimeStatus>('idle');
    get status() {
        return this._status.asReadonly();
    }

    private readonly _organizationId = signal<number | null>(null);
    get organizationId() {
        return this._organizationId.asReadonly();
    }

    private readonly _tradingProcedure = signal<TradingProcedure | null>(null);
    get tradingProcedure() {
        return this._tradingProcedure.asReadonly();
    }

    private readonly _itemRefsById = signal<Map<Id, LotItemRef> | null>(null);
    get itemRefsById() {
        return this._itemRefsById.asReadonly();
    }

    private readonly _role = signal<ParticipantRole | null>(null);
    get role() {
        return this._role.asReadonly();
    }

    private readonly _bidHistoriesByAuctionId = signal<Map<number, BidHistory[]>>(new Map());
    get bidHistoriesByAuctionId() {
        return this._bidHistoriesByAuctionId.asReadonly();
    }

    private readonly _offerResultsById = signal<Map<number, OfferResult>>(new Map());
    get offerResultsById() {
        return this._offerResultsById.asReadonly();
    }

    private readonly _organizationNameById = signal<Record<number, string>>({});
    get organizationNameById() {
        return this._organizationNameById.asReadonly();
    }

    private readonly autobidSetEvent$: Subject<AutoBid> = new Subject();
    private readonly autobidRemovedEvent$: Subject<Id> = new Subject();
    private readonly allAuctionsFinished$: Subject<void> = new Subject();

    // Событие смены статуса ТП может прийти по сокету до того, как снапшот загружен.
    // Сохраняем его здесь и применяем сразу после onInitLoad.
    private _pendingStatusEvent: TradingProcedureStatusChanged | null = null;

    private readonly _connectionStatus = signal<ConnectionStatus>('connecting');
    get connectionStatus() {
        return this._connectionStatus.asReadonly();
    }

    init(tradingProcedureId: number) {
        this._status.set('loading');

        const load$: Observable<Result<TPEvent, Error>> = this.tradingQuery.getTradingProcedureSnapshot(tradingProcedureId).pipe(
            map((result) => {
                return result.map((snapshot) => ({
                    kind: 'InitLoad',
                    tradingProcedureSnapshot: snapshot
                } as TPEvent)).mapErr(err => new Error(`Failed to load trading procedure data: ${err.message}`));
            }),
            take(1),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const ready$ = load$.pipe(
            filter(r => r.isOk()),
            take(1),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const socketEvents$: Observable<Result<TPEvent, Error>> = merge(
            this.tradingEvents.auctionStarted$.pipe(
                filter((ev) => this.lotProcessStore.itemRefsById.has(ev.id)),
                mergeMap((ev) => this.tradingQuery.getAuctionById(ev.id)),
                map((result) =>
                    result
                        .map((auction) => ({ kind: 'AuctionStarted', auction } as AuctionStartedEvent))
                        .mapErr((err) => new Error(`Failed to get auction by id: ${err.message}`))
                )
            ),
            this.tradingEvents.auctionExtended$.pipe(
                filter((ev) => this.lotProcessStore.itemRefsById.has(ev.id)),
                map((extendedResponse) =>
                    ok<AuctionExtendedEvent, Error>({
                        kind: 'AuctionExtended',
                        auctionExtendedPayload: extendedResponse,
                    })
                )
            ),
            this.tradingEvents.auctionEnded$.pipe(
                filter((ev) => this.lotProcessStore.itemRefsById.has(ev.id)),
                map((endedResponse) =>
                    ok<AuctionEndedEvent, Error>({ kind: 'AuctionEnded', auctionId: endedResponse.id })
                )
            ),
            this.tradingEvents.bidAccepted$.pipe(
                filter((ev) => this.lotProcessStore.itemRefsById.has(ev.auctionId)),
                map((bidAccepted: BidAccepted) =>
                    ok<BidAcceptedEvent, Error>({
                        kind: 'BidAccepted',
                        ba: bidAccepted,
                    })
                )
            )
        );

        // Эвенты, которые не прокидываются через сокеты
        const customEvents$: Observable<Result<TPEvent, Error>> = merge(
            this.autobidSetEvent$.pipe(map((autobid) => ok<AutoBidSetEvent, Error>({ kind: 'AutoBidSet', autobid }))),
            this.autobidRemovedEvent$.pipe(
                map((id) =>
                    ok<AutoBidDeletedEvent, Error>({
                        kind: 'AutoBidDeleted',
                        lotId: id,
                    })
                )
            )
        );

        const events$ = concat(
            load$,
            merge(socketEvents$, customEvents$).pipe(skipUntil(ready$))
        );

        //TODO стартовать только когда ТП в нужном статусе
        this.tradingEvents.startConnections();

        events$.pipe(takeUntil(this.destroy$)).subscribe((eventResult) => {
            action(eventResult, this.handleEventSuccess.bind(this), this.handleEventError.bind(this));
        });

        // Событие смены статуса ТП обрабатываем отдельно от основного потока событий,
        // потому что оно может прийти до того, как снапшот загружен.
        this.tradingEvents.tradingProcedureStatusChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                if (!this._tradingProcedure()) {
                    this._pendingStatusEvent = event;
                    return;
                }
                if (this._tradingProcedure()?.id !== event.id) return;
                this._tradingProcedure.update((prev) => (prev ? { ...prev, status: event.status } : prev));
                if (this._role() === 'seller' && SELLER_DEANON_STATUSES.includes(event.status)) {
                    const tpId = this._tradingProcedure()?.id;
                    if (tpId) this.loadSellerOrganizationInfos(tpId);
                }
            });

        // Reconnect — полная ресинхронизация снапшота
        this.tradingEvents.reconnected$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            const tp = this._tradingProcedure();
            if (!tp) return;
            this._status.set('loading');
            this.lotProcessStore.clear();
            this.tradingQuery.getTradingProcedureSnapshot(tp.id).pipe(take(1)).subscribe((result) => {
                action(result,
                    (snapshot) => this.handleEventSuccess({ kind: 'InitLoad', tradingProcedureSnapshot: snapshot }),
                    (err) => this.handleEventError(err)
                );
            });
        });

        this.tradingEvents.connectionStatus$.pipe(takeUntil(this.destroy$))
            .subscribe((status) => this._connectionStatus.set(status));
    }

    createBid(auctionId: number): Observable<Result<void, Error>> {
        const lotProcess = this.lotProcessStore.itemRefsById.get(auctionId)?.model();

        if (!lotProcess) {
            return of(err<void, Error>(new Error(`Auction with id ${auctionId} not found`)));
        }

        if (!isActive(lotProcess)) {
            return of(err<void, Error>(new Error(`Auction with id ${auctionId} is not active`)));
        }

        // Если ещё никто не ставил, то мы не двигаем итерацию
        const payload: CreateBidPayload = {
            auctionId: auctionId,
            iteration: lotProcess.auction.leadingOrganizationId === null ? 0 : lotProcess.auction.currentIteration + 1,
        };

        return this.tradingCommand.createBid(payload);
    }

    setAutoBid(payload: SetAutoBidPayload): Observable<Result<void, Error>> {
        const lotProcess = this.lotProcessStore.itemRefsById.get(payload.lotId)?.model();

        if (!lotProcess) {
            return of(err<void, Error>(new Error(`Lot with id ${payload.lotId} not found`)));
        }

        if (lotProcess.kind === 'FinishedAuction') {
            return of(err<void, Error>(new Error(`Lot with id ${payload.lotId} is finished`)));
        }

        return this.tradingCommand.setAutoBid(payload)
            .pipe(
                onOk((autoBid) => this.autobidSetEvent$.next(autoBid)),
                map(() => ok<void, Error>(void 0)));
    }

    deleteAutoBid(lotId: Id): Observable<Result<void, Error>> {
        const lotProcess = this.lotProcessStore.itemRefsById.get(lotId)?.model();
        if (!lotProcess) {
            return of(err<void, Error>(new Error(`Lot with id ${lotId} not found`)));
        }

        if (lotProcess.kind === 'FinishedAuction') {
            return of(err<void, Error>(new Error(`AutoBid for lot with id ${lotId} not found`)));
        }

        const autobidId = lotProcess.autobid?.id;

        if (!autobidId) {
            return of(err<void, Error>(new Error(`AutoBid for lot with id ${lotId} not found`)));
        }

        return this.tradingCommand.removeAutoBid(autobidId).pipe(
            onOk(() => this.autobidRemovedEvent$.next(lotProcess.lot.id)),
            map(() => ok<void, Error>(void 0))
        );
    }

    getTradingProcedureDocuments(): Observable<Result<TradingProcedureDocument[], Error>> {
        return toObservable(this._tradingProcedure).pipe(
            filter(tp => tp !== null),
            switchMap(tp => this.tradingQuery.getTradingProcedureDocuments(tp.id))
        )
    }

    getTradingProcedureRegistry(): Observable<Result<TradingProcedureRegistry, Error>> {
        return toObservable(this._tradingProcedure).pipe(
            filter(tp => tp !== null),
            switchMap(tp => this.tradingQuery.getTradingProcedureRegistry(tp.id))
        )
    }

    downloadRegistryFile(tpId: number): Observable<Result<Blob, Error>> {
        return this.tradingQuery.getRegistryFile(tpId);
    }

    private handleEventError(err: Error) {
        console.error(err);
        this._status.set('error');
    }

    private handleEventSuccess(event: TPEvent) {
        switch (event.kind) {
            case 'InitLoad':
                this.onInitLoad(event);
                break;
            case 'AuctionExtended':
                this.onAuctionExtended(event);
                break;
            case 'AuctionEnded':
                this.onAuctionEnded(event);
                break;
            case 'AuctionStarted':
                this.onAuctionStarted(event);
                break;
            case 'BidAccepted':
                this.onBidAccepted(event);
                break;
            case 'AutoBidSet':
                this.onAutoBidSet(event);
                break;
            case 'AutoBidDeleted':
                this.onAutoBidDeleted(event);
                break;
            default:
                exhaustiveCheck(event);
        }

        this._status.set('ok');
    }

    loadBidHistories(auctionId: number): void {
        if (this._bidHistoriesByAuctionId().has(auctionId)) return;

        this.tradingQuery.getBidHistories(auctionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (!result.isOk()) return;
                this._bidHistoriesByAuctionId.update((map) => {
                    const next = new Map(map);
                    next.set(auctionId, result.unwrap());
                    return next;
                });
            });

        this.tradingEvents.bidHistoryCreated$.pipe(
            filter((msg) => msg.auctionId === auctionId),
            takeUntil(this.destroy$)
        ).subscribe((msg) => {
            this._bidHistoriesByAuctionId.update((map) => {
                const existing = map.get(auctionId);
                if (!existing) return map;
                const next = new Map(map);
                next.set(auctionId, [...existing, {
                    id: msg.id,
                    auctionId: msg.auctionId,
                    organizationId: msg.organizationId,
                    postedDateTime: msg.postedDateTime,
                    iteration: msg.iteration,
                }]);
                return next;
            });
        });
    }

    loadOfferResults(tpId: number): void {
        this.tradingQuery.getOfferResults(tpId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (!result.isOk()) return;
                const map = new Map<number, OfferResult>();
                for (const offer of result.unwrap()) {
                    map.set(offer.id, offer);
                }
                this._offerResultsById.set(map);
            });
    }

    private loadSellerOrganizationInfos(tpId: number): void {
        this.tradingQuery.getSellerOrganizationInfos(tpId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (!result.isOk()) return;
                const incoming: Record<number, string> = {};
                for (const info of result.unwrap()) {
                    if (info.organizationName) {
                        incoming[info.organizationId] = info.organizationName;
                    }
                }
                this._organizationNameById.update((prev) => ({ ...prev, ...incoming }));
            });
    }

    dispose(): void {
        this.destroy$.next();
        this.tradingEvents.stopConnections()
        this.lotProcessStore.clear();
    }

    private onInitLoad(event: InitLoadEvent) {
        const { organizationId, organizationName, role, tradingProcedure, lots, auctions, autoBids, offers } = event.tradingProcedureSnapshot;

        this._organizationId.set(organizationId);
        this._role.set(role);
        this._tradingProcedure.set(tradingProcedure);

        // RC3: применяем накопленное событие статуса если пришло до загрузки снапшота
        if (this._pendingStatusEvent && this._pendingStatusEvent.id === tradingProcedure.id) {
            const pending = this._pendingStatusEvent;
            this._pendingStatusEvent = null;
            this._tradingProcedure.update((prev) => prev ? { ...prev, status: pending.status } : prev);
            if (role === 'seller' && SELLER_DEANON_STATUSES.includes(pending.status)) {
                this.loadSellerOrganizationInfos(tradingProcedure.id);
            }
        } else {
            this._pendingStatusEvent = null;
            if (role === 'seller' && SELLER_DEANON_STATUSES.includes(tradingProcedure.status)) {
                this.loadSellerOrganizationInfos(tradingProcedure.id);
            }
        }

        if (organizationName) {
            this._organizationNameById.set({ [organizationId]: organizationName });
        }

        this.lotProcessStore.upsertLots(lots);

        const activeLotIds   = new Set<number>();
        const finishedLotIds = new Set<number>();

        for (const a of auctions) {
            if (a.status === 'active')   activeLotIds.add(a.id);
            if (a.status === 'finished') finishedLotIds.add(a.id);
        }

        for (const ab of autoBids) {
            const lotId = ab.lotId;
            if (finishedLotIds.has(lotId)) continue; // в Finished автоставок быть не должно

            this.lotProcessStore.setAutoBid(lotId, ab);
        }

        for (const a of auctions) {
            if (a.status === 'finished') {
                this.lotProcessStore.finishAuction(a);
            }
        }

        for (const a of auctions) {
            if (a.status === 'active') {
                this.lotProcessStore.activateAuction(a);
            }
        }

        for (const offer of offers) {
            const lotId = offer.lotId;

            // TODO store сам отсеет не стартовавшие аукционы, но нужно сделать это более явно
            this.lotProcessStore.setCurrentOffer(lotId, offer);
        }

        this._itemRefsById.set(this.lotProcessStore.itemRefsById);
    }

    private onAuctionExtended(event: AuctionExtendedEvent) {
        const { id, endDateTime } = event.auctionExtendedPayload;

        this.lotProcessStore.patchActiveAuction(id, { endDateTime: new Date(endDateTime) });
    }

    private onAuctionEnded(event: AuctionEndedEvent) {
        const auctionId = event.auctionId;
        const lotProcess = this.lotProcessStore.itemRefsById.get(auctionId)?.model();

        if (!lotProcess) {
            return;
        }

        if (!isActive(lotProcess)) {
            return;
        }

        this.lotProcessStore.finishAuction(lotProcess.auction);

        this.checkForTPFinished();
    }

    private onAuctionStarted(event: AuctionStartedEvent) {
        const { auction } = event;
        const lotProcess = this.lotProcessStore.itemRefsById.get(auction.id)?.model();

        if (!lotProcess) {
            return;
        }

        if (!isNotStarted(lotProcess)) {
            return;
        }

        // TODO автобид может быть, когда эта хуйня ещё не началась !!!!!
        this.lotProcessStore.activateAuction(auction);
    }

    private onBidAccepted(event: BidAcceptedEvent) {
        const { auctionId, organizationId, iteration } = event.ba;

        this.lotProcessStore.patchActiveAuction(auctionId, {
            leadingOrganizationId: organizationId,
            currentIteration: iteration,
        });

        // TODO не нравится мне такая логика, тоже не очевидно
        if (organizationId === this._organizationId()) {
            const lotProcess = this.lotProcessStore.itemRefsById.get(auctionId)?.model();
            const tradingProcedure = this._tradingProcedure();

            if (lotProcess && tradingProcedure) {
                const lot = lotProcess.kind === 'NotStartedAuction' ? lotProcess.lot :
                           lotProcess.kind === 'ActiveAuction' ? lotProcess.lot :
                           lotProcess.lot;

                // TODO: bidStepAmount is always set for English auctions, but the type allows null (shared DTO)
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const amount = calculateCurrentBid(lot.minPrice, tradingProcedure.bidStepAmount!, iteration);

                this.lotProcessStore.setCurrentOffer(auctionId, {
                    lotId: auctionId,
                    amount: amount,
                    iteration: iteration
                });
            }
        }
    }

    private onAutoBidSet(event: AutoBidSetEvent) {
        this.lotProcessStore.setAutoBid(event.autobid.lotId, event.autobid);
    }

    private onAutoBidDeleted(event: AutoBidDeletedEvent) {
        this.lotProcessStore.removeAutobid(event.lotId);
    }

    private checkForTPFinished() {
        const allFinished = Array.from(this.lotProcessStore.itemRefsById.values())
            .every(ref => isFinished(ref.model()));

        if (allFinished) {
            this.allAuctionsFinished$.next()
        }
    }
}

export type RuntimeStatus = 'idle' | 'loading' | 'ok' | 'error';

export type InitLoadEvent = {
    kind: 'InitLoad';
    tradingProcedureSnapshot: TradingProcedureSnapshot;
};
export type AuctionStartedEvent = { kind: 'AuctionStarted'; auction: Auction };
export type AuctionEndedEvent = { kind: 'AuctionEnded'; auctionId: Id };
export type AuctionExtendedEvent = { kind: 'AuctionExtended'; auctionExtendedPayload: AuctionExtended };

export type BidAcceptedEvent = { kind: 'BidAccepted'; ba: BidAccepted };

export type AutoBidSetEvent = { kind: 'AutoBidSet'; autobid: AutoBid };
export type AutoBidDeletedEvent = { kind: 'AutoBidDeleted'; lotId: Id };

type TPEvent =
    | InitLoadEvent
    | AuctionStartedEvent
    | AuctionEndedEvent
    | AuctionExtendedEvent
    | BidAcceptedEvent
    | AutoBidSetEvent
    | AutoBidDeletedEvent
